import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Chip, FormControl, FormHelperText, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { useCustomers } from 'features/customers/hooks/useCustomers'
import debounce from 'lodash/debounce'

import { input } from '@albacross/frontend-utils'

type CustomerMultiSelectProps = {
  value?: any
  onBlur: (...args: any[]) => any
  onChange: (...args: any[]) => any
  onFocus: (...args: any[]) => any
  className?: string
  disabled?: boolean
  errorId?: string | boolean
  fullWidth?: boolean
  label?: string
}

const CustomerMultiSelect = (props: CustomerMultiSelectProps) => {
  const { className, disabled, errorId, label, fullWidth = true, value = [], onFocus, onBlur, onChange } = props

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [selectedCustomersMap, setSelectedCustomersMap] = useState<Record<string, any>>({})

  // Debounced function to update debouncedSearch
  const debouncedSetSearch = useCallback(
    debounce((value: any) => {
      setDebouncedSearch(value)
    }, 300),
    [],
  )

  // Update debouncedSearch when search changes
  useEffect(() => {
    debouncedSetSearch(search)
  }, [search, debouncedSetSearch])

  // Create meta object for useCustomers hook
  const meta = useMemo(
    () => ({
      rowsPerPage: 50,
      page: 0,
      sortBy: 'created_at',
      sortOrder: 'desc',
      filters: {
        search: debouncedSearch,
      },
    }),
    [debouncedSearch],
  )

  // Use the hook to fetch customers
  const { data, isLoading: loading } = useCustomers(meta)

  // Process customers data
  const options = useMemo(() => {
    if (!data?.items) return []

    return data.items.slice(0, 50).map(({ id, name }: any) => ({
      id,
      name,
    }))
  }, [data])

  // Update selected customers map when options change
  useEffect(() => {
    if (options.length > 0 && value) {
      setSelectedCustomersMap(prevMap => {
        const newMap = { ...prevMap }
        options.forEach((customer: any) => {
          if (value.includes(customer.id)) {
            newMap[customer.id] = customer
          }
        })
        return newMap
      })
    }
  }, [options, value])

  const handleChange = (_event: any, newValue: any) => {
    const newMap: Record<string, any> = {}
    newValue.forEach((customer: any) => {
      newMap[customer.id] = customer
    })
    setSelectedCustomersMap(newMap)

    onChange(newValue.map((customer: any) => customer.id))

    // Clear search input when a value is selected
    setSearch('')
  }

  const handleInputChange = (_event: any, newInputValue: any, reason: any) => {
    // Update search state for all input changes
    if (reason === 'input' || reason === 'clear' || !reason) {
      setSearch(newInputValue || '')
    }
  }

  const getOptionLabel = ({ name, id }: any) => `${name} (${id})`

  const getOptionSelected = (option: any, selectedValue: any) => {
    if (!selectedValue || !Array.isArray(selectedValue)) return false
    return selectedValue.some((customer: any) => customer.id === option.id)
  }

  // Map selected IDs to customer objects for Autocomplete, falling back to the cached map (or a placeholder) for IDs not in the current page.
  const selectedCustomers =
    value && value.length > 0
      ? value.map((id: any) => {
          const inOptions = options.find((customer: any) => customer.id === id)
          if (inOptions) return inOptions
          return selectedCustomersMap[id] || { id, name: `Unknown Customer (${id})` }
        })
      : []

  return (
    <FormControl className={className} disabled={disabled} error={!!errorId} fullWidth={fullWidth}>
      <Autocomplete
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={getOptionSelected}
        inputValue={search}
        loading={loading}
        multiple
        onChange={handleChange}
        onClose={_e => {
          setOpen(false)
          onBlur()
        }}
        onInputChange={handleInputChange}
        onOpen={e => {
          setOpen(true)
          onFocus(e)
        }}
        open={open}
        options={options}
        renderInput={params => <TextField {...params} error={!!errorId} label={label} variant="outlined" />}
        renderValue={(value: any, getItemProps: any) =>
          value.map((option: any, index: number) => {
            const { onDelete } = getItemProps({ index }) as any
            return (
              <Chip
                key={option.id}
                label={getOptionLabel(option)}
                onDelete={onDelete}
                size="small"
                style={{ margin: '2px' }}
              />
            )
          })
        }
        value={selectedCustomers}
      />
      {errorId && <FormHelperText error>{errorId}</FormHelperText>}
    </FormControl>
  )
}

export default input([])(CustomerMultiSelect)
